/**
 * Full-screen sync overlay for the SolidJS frontend — equivalent of
 * `apps/native/shared/screens/auth/initial-sync-screen.tsx`.
 *
 * Drives the agnostic `initialSyncMachine` from `@bso/shared` with three
 * sources of truth:
 *   1. Session state (sessionActor) — `hasUser`, `inAuthGroup`.
 *   2. PowerSync status (`usePowerSyncStatus`) — `hasSynced`, `isDownloading`,
 *      `isConnected/Connecting`, `isOnline`, download progress.
 *   3. TanStack Router location — to derive `inAuthGroup`.
 *
 * Renders children always (no conditional unmount) and stacks the overlay on
 * top while `showFullScreen` is true. Falls back to a minimised floating
 * toast once the user dismisses the overlay (the machine still in
 * `connecting`/`downloading`).
 */

import { fromActorRef } from "@fleet/state-machines/solid";
import { useLocation } from "@tanstack/solid-router";
import { type ActorRefFrom, createActor } from "xstate";
import {
  getInitialSyncUI,
  initialSyncMachine,
  type InitialSyncStateName,
  initialSyncStatusEvent,
  initialSyncUserEvents,
  mapHasLocalData,
  nextFirstSyncPercent,
  readEverSynced,
  type SyncHistory,
  VISITOR_ID,
} from "@bso/shared";
import { getHasLocalDataQuery } from "@bso/powersync";
import { msg } from "@lingui/core/macro";
import {
  createEffect,
  createMemo,
  createSignal,
  on,
  onCleanup,
  onMount,
  type ParentComponent,
  Show,
  untrack,
} from "solid-js";
import { Portal } from "solid-js/web";

import { Loader2, WifiOff } from "@fleet/icons";

import { usePowerSyncContext } from "@/providers/power-sync-context";
import { createExtendedPowerSyncStatus } from "@bso/shared/solid";
import { Button } from "@/components/ui/button";
import { appState, GlassSurface, Transition } from "@fleet/ui";
import { queryClient } from "@/lib/query-client";

import { useI18n } from "@/providers/i18n-provider";
import { useSignOut } from "@/providers/boot-manager";
import { sessionActor } from "@/lib/session-machine";

const AUTH_PATH_PREFIXES = ["/login", "/register", "/forgot-password", "/reset-password"];

function isInAuthGroup(pathname: string): boolean {
  return AUTH_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export const InitialSyncOverlay: ParentComponent = (props) => {
  const ctx = usePowerSyncContext();
  const sessionSnap = fromActorRef(sessionActor);
  const status = createExtendedPowerSyncStatus(() => ctx.db());
  const location = useLocation();
  const { t } = useI18n();
  const { promptQuickSignOut, isChecking, isSigningOut } = useSignOut();

  // Local actor for the initial-sync state machine.
  const actor: ActorRefFrom<typeof initialSyncMachine> = createActor(initialSyncMachine);
  onMount(() => {
    actor.start();
  });
  onCleanup(() => {
    actor.stop();
  });

  const machineSnap = fromActorRef(actor);
  const stateName = (): InitialSyncStateName => machineSnap().value as InitialSyncStateName;
  const ui = createMemo(() => getInitialSyncUI(stateName()));

  // Derived flags
  const userId = () => sessionSnap().context.session?.user.id ?? VISITOR_ID;
  const isPriming = () => sessionSnap().matches("priming");
  const hasUser = () => !isPriming() && userId() !== VISITOR_ID;
  const inAuthGroup = () => isInAuthGroup(location().pathname);

  const [hasLocalData, setHasLocalData] = createSignal(false);
  // The user tapped the pill to view the details again. It takes precedence over
  // the heuristics that hide the card (local data present, auto-collapse):
  // if they explicitly asked, it stays shown until they close it or
  // synchronization finishes.
  const [reopenedByUser, setReopenedByUser] = createSignal(false);

  createEffect(() => {
    const database = ctx.db();
    const uid = userId();
    if (!database || !uid || uid === VISITOR_ID) {
      return;
    }

    void (async () => {
      try {
        const q = getHasLocalDataQuery(uid);
        const rows = await database.getAll<{ hasData: number }>(q.sql, q.parameters);
        setHasLocalData(mapHasLocalData(rows));
      } catch (err) {
        console.warn("[InitialSyncOverlay] Failed to check for local data:", err);
      }
    })();
  });

  // Has THIS database ever completed a sync? Read from SQLite
  // (`powersync_offline_sync_status()`), not from the live status: the live
  // stream list is empty on every cold start until the sync client emits its
  // first status line, which walked the machine into `connecting` and showed
  // the first-sync card to users who had synced long ago (every start under
  // Tauri, and for as long as the sync service was unreachable).
  //   "unknown" → not answered yet: the machine is held in `passthrough`.
  //   "synced"  → never show the first-sync UI for this database.
  //   "never"   → a genuine first sync (or the query failed: fall back to the
  //               live status, which is the behaviour before this existed).
  const [syncHistory, setSyncHistory] = createSignal<SyncHistory>("unknown");

  createEffect(() => {
    const database = ctx.db();
    const uid = userId();
    setSyncHistory("unknown");
    if (!database || !uid || uid === VISITOR_ID) {
      return;
    }
    const request = { stale: false };
    onCleanup(() => {
      request.stale = true;
    });
    void (async () => {
      const synced = await readEverSynced(database);
      if (!request.stale) {
        setSyncHistory(synced === true ? "synced" : "never");
      }
    })();
  });

  const hasSyncedEffective = () => status().hasSynced || syncHistory() === "synced";

  // The machine's `synced` state is `type: "final"` — XState stops the
  // actor automatically when it reaches it. Sending more events afterwards
  // logs a noisy "Event was sent to stopped actor" warning on every
  // PowerSync status tick. Gate the sends.
  const isMachineDone = () => stateName() === "synced";

  // When the initial sync completes we proactively invalidate every cached

  // query in TanStack so any list/dashboard component that mounted with
  // empty PowerSync results refetches against the now-populated SQLite —
  // otherwise the user sees the overlay disappear over a stale render and
  // has to refresh the page to see their data. Only fires once because
  // `synced` is final.
  let invalidatedAfterSync = false;
  createEffect(() => {
    if (stateName() === "synced" && !invalidatedAfterSync) {
      invalidatedAfterSync = true;
      void queryClient.invalidateQueries();
    }
  });

  // Auto-minimise the overlay so the user can interact with the app while
  // sync continues in the background.
  //
  // Uses an absolute deadline (MAX_BLOCKING_MS from when the overlay first
  // appeared) rather than resetting the timer on every state transition.
  // Without this, PowerSync's connecting→downloading oscillations would
  // reset the 4 s timer indefinitely, causing the overlay to block for the
  // full 10 s until the machine enters `stuck`.
  const MAX_BLOCKING_MS = 5000;
  let firstBlockingTime: number | null = null;

  const shouldShowFullScreenRaw = createMemo(() => {
    // 1. While signing out, keep the overlay up to show feedback
    if (isSigningOut()) {
      return true;
    }

    // 2. Auth pages (Login, etc.) never need the blocking sync overlay
    if (inAuthGroup()) {
      return false;
    }

    // 3. While priming (checking session), show the boot loader until app is ready
    if (isPriming()) {
      return !appState.isReady();
    }

    // 4. If no user is logged in, there is nothing to sync
    if (!hasUser()) {
      return false;
    }

    const isSynced = ui().showFullScreen;
    const isDbReady = ctx.db() !== null;

    // 5. If we have local data, we don't need to wait for PowerSync to complete its sync loop.
    // We only need the database object to be ready and the app layout to be mounted.
    if (hasLocalData()) {
      return !isDbReady || !appState.isReady();
    }

    // 6. Otherwise, if there is no local data, the overlay stays up until:
    //    - The machine says we are synced
    //    - The database object is actually available to the UI
    //    - The application has signaled it is mounted and ready
    return isSynced || !isDbReady || !appState.isReady();
  });

  const [shouldShowFullScreen, setShouldShowFullScreen] = createSignal(
    untrack(shouldShowFullScreenRaw),
  );

  let lastShowTime = 0;
  const MIN_VISIBLE_MS = 400;

  createEffect(() => {
    const val = shouldShowFullScreenRaw();
    if (val) {
      // Show immediately
      setShouldShowFullScreen(true);
      lastShowTime = Date.now();
    } else {
      // Ensure it stays visible for at least MIN_VISIBLE_MS to avoid blinks
      const elapsed = Date.now() - lastShowTime;
      const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);

      if (remaining > 0) {
        const handle = setTimeout(() => {
          setShouldShowFullScreen(false);
        }, remaining);
        onCleanup(() => {
          clearTimeout(handle);
        });
      } else {
        setShouldShowFullScreen(false);
      }
    }
  });

  createEffect(() => {
    const meta = ui();

    if (!meta.showFullScreen) {
      firstBlockingTime = null;
      return;
    }
    if (meta.isOffline) {
      return; // offline genuinely needs user attention — don't auto-dismiss
    }
    if (reopenedByUser()) {
      // They reopened it manually: auto-collapsing it again after 5 s would fight them.
      return;
    }
    firstBlockingTime ??= Date.now();
    const elapsed = Date.now() - firstBlockingTime;
    const remaining = Math.max(0, MAX_BLOCKING_MS - elapsed);
    const handle = setTimeout(() => {
      actor.send({ type: "MINIMIZE" });
    }, remaining);
    onCleanup(() => {
      clearTimeout(handle);
    });
  });

  // ─── Feed USER_CHANGED into the machine ────────────────────────────────
  const sendStatus = (s: ReturnType<typeof status>) => {
    actor.send(initialSyncStatusEvent(s, syncHistory()));
  };

  // Held (empty list) until the local database has answered; a database that
  // already synced gets STATUS_CHANGED(hasSynced) BEFORE USER_CHANGED so the
  // machine never visits `connecting`. See `initial-sync-gate.ts`.
  createEffect(
    on([hasUser, inAuthGroup, syncHistory], ([h, a, prior]) => {
      const events = initialSyncUserEvents({
        hasUser: h,
        inAuthGroup: a,
        syncHistory: prior,
        live: untrack(status),
      });
      for (const event of events) {
        if (isMachineDone()) {
          return;
        }
        actor.send(event);
      }
    }),
  );

  // ─── Feed STATUS_CHANGED into the machine ──────────────────────────────
  createEffect(
    on(status, (s) => {
      if (isMachineDone()) {
        return;
      }
      sendStatus(s);
    }),
  );

  createEffect(() => {
    if (!shouldShowFullScreen()) {
      appState.markBootComplete();
    }
  });

  const handleMinimize = () => {
    setReopenedByUser(false);
    actor.send({ type: "MINIMIZE" });
  };

  /** Reopens the details from the floating pill. */
  const handleReopen = () => {
    setReopenedByUser(true);
    actor.send({ type: "MAXIMIZE" });
  };

  // Description text — uses Lingui so it follows the user's selected locale.
  const description = createMemo(() => {
    if (isSigningOut()) {
      return t(msg`Cleaning up local data...`);
    }
    const s = status();
    const meta = ui();
    const desc = meta.descriptionKey;
    if (desc === "connecting") {
      return t(msg`Connecting to server...`);
    }
    if (desc === "downloading") {
      const ops = s.downloadedOperations;
      const total = s.totalOperations;
      if (ops !== null && total !== null && total > 0) {
        return t(msg`Downloaded ${ops} out of ${total}`);
      }
      return t(msg`Downloading account and tasks...`);
    }
    if (desc === "offline") {
      return t(msg`Please connect to the internet to download your tasks for the first time.`);
    }
    if (desc === "stuck") {
      if (s.downloadedFraction === 1) {
        return t(msg`Finishing local data processing, almost ready...`);
      }
      return t(msg`Slow connection or server busy, please wait...`);
    }
    if (desc === "syncing") {
      return t(msg`Syncing...`);
    }
    return "";
  });

  const title = createMemo(() => {
    if (isSigningOut()) {
      return t(msg`Signing out`);
    }
    const meta = ui();
    if (meta.titleKey === "offline") {
      return t(msg`You are offline`);
    }
    if (meta.titleKey === "settingUp") {
      return t(msg`Setting up your workspace...`);
    }
    return "";
  });

  // Displayed percentage: monotonic, 100 only once the sync completed, and
  // sticky between download iterations (the raw fraction goes null the moment
  // an iteration ends, which made the bar and the pill vanish before 100%).
  // `null` = workload unknown → indeterminate, never a fake "0%".
  const displayedPercent = createMemo<number | null>(
    (previous) =>
      nextFirstSyncPercent(previous, {
        downloadedFraction: status().downloadedFraction,
        totalOperations: status().totalOperations,
        hasSynced: hasSyncedEffective() || isMachineDone(),
      }),
    null,
  );
  const progressPercent = () => displayedPercent() ?? 0;

  const showModalCard = createMemo(() => {
    const state = stateName();
    if (isSigningOut() || state === "offline" || state === "stuck") {
      return true;
    }
    // Explicit reopening from the pill: it takes precedence over everything else.
    if (reopenedByUser()) {
      return true;
    }
    // TRUE first sync only: no local rows yet, so the shell's skeletons have
    // nothing to render and the user needs to see that work is happening and
    // how far along it is. Once there IS local data the app stays skeleton-first
    // and syncs silently in the background — the card would only be noise.
    return ui().showFullScreen && !hasLocalData();
  });

  const showMinimisedPill = createMemo(() => {
    if (isSigningOut() || !hasUser() || inAuthGroup()) {
      return false;
    }
    if (hasSyncedEffective() || isMachineDone()) {
      return false;
    }
    if (displayedPercent() === null) {
      return false;
    }
    return !(shouldShowFullScreen() && showModalCard());
  });

  return (
    <div class="relative h-full min-h-dvh w-full" data-initial-sync-state={stateName()}>
      {/*
        Always keep children mounted — no Show wrapper — to avoid unmount/remount cost.
        During initial sync we use `content-visibility: hidden` which tells the browser
        to skip layout, paint and hit-testing for the entire subtree. This is cheaper
        than opacity:0 + visibility:hidden because the browser doesn't even build a
        paint layer for it. The subtree stays connected to the DOM so SolidJS signals
        are still live, but the browser skips all rendering work.
      */}
      {/*
        Skeleton-first: el shell REAL queda SIEMPRE visible (sin `content-visibility:
        hidden`/`opacity-0`). Durante la sync inicial, cada sección muestra su propio
        skeleton granular (local-first: los datos locales aparecen al instante); el
        progreso de la sync se comunica con la tarjeta flotante NO bloqueante de abajo.
      */}
      <div class="h-full w-full">{props.children}</div>

      {/*
        Minimised progress pill. The overlay auto-minimises after MAX_BLOCKING_MS
        so the user is never blocked, but a first sync of tens of thousands of
        operations takes far longer than that — without this the percentage just
        vanished and the app looked idle while it was still downloading. The
        machine has always declared `showMinimizedToast` for this; nothing
        rendered it until now.
      */}
      <Portal>
        <Transition name="fade">
          <Show when={showMinimisedPill()}>
            <div class="fixed bottom-4 right-4 z-[70]">
              {/*
                Botón, no adorno: mientras la primera carga siga en marcha tiene
                que haber SIEMPRE algo visible, y tocarlo devuelve el detalle
                (progreso, estado y las acciones). Es el único camino de vuelta
                tras cerrar la tarjeta, así que es interactivo a propósito.
              */}
              <button
                type="button"
                onClick={handleReopen}
                aria-label={t(msg`Show sync details`)}
                data-testid="initial-sync-progress-minimised"
                data-percent={progressPercent()}
                // The same counters as the card's bar: when the card was
                // auto-minimised before the download started (a connect slower
                // than MAX_BLOCKING_MS), this pill is the ONLY surface showing
                // the first-sync percentage, so it must be observable the same
                // way (`e2e/initial-sync-progress.spec.ts` samples both).
                data-downloaded={status().downloadedOperations ?? ""}
                data-total={status().totalOperations ?? ""}
                data-streams-synced={status().syncedStreamCount}
                data-streams-total={status().streamCount}
                class="cursor-pointer rounded-full outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                <GlassSurface
                  class="flex items-center gap-2.5 px-3 py-2 shadow-lg bg-background/60 border-border/20 transition-transform active:scale-95"
                  disableBlur={false}
                >
                  <Loader2 class="size-3.5 animate-spin text-accent shrink-0" />
                  <span class="text-xs tabular-nums text-muted-foreground">
                    {t(msg`Syncing...`)} {progressPercent()}%
                  </span>
                </GlassSurface>
              </button>
            </div>
          </Show>
        </Transition>
      </Portal>

      {/* Floating status card portal on top of the root skeleton */}
      <Portal>
        <Transition name="fade">
          <Show when={shouldShowFullScreen() && showModalCard()}>
            {/*
              Backdrop REAL. Antes era `bg-transparent pointer-events-none`: la
              tarjeta flotaba sobre la interfaz normal y no se leía como modal.
              Ahora atenúa y desenfoca el fondo y captura el puntero, que es lo
              que distingue un modal de un aviso flotante. Sigue habiendo
              salida: el botón «Sincronizar en segundo plano» lo repliega a la
              píldora.
            */}
            <div class="fixed inset-0 z-[80] flex items-center justify-center bg-background/70 backdrop-blur-sm pointer-events-auto">
              {/* Glassmorphic status card floating on top */}
              <GlassSurface
                data-testid="initial-sync-card"
                class="pointer-events-auto relative w-[calc(100%-2rem)] max-w-md mx-4 p-6 shadow-2xl bg-background/50 border-border/20"
                disableBlur={false}
              >
                <div class="flex flex-col items-center text-center gap-5 w-full">
                  {/* Loader icon */}
                  <div class="relative flex items-center justify-center">
                    <Show
                      when={ui().isOffline}
                      fallback={
                        <div class="relative flex size-16 items-center justify-center rounded-2xl border border-accent/20 bg-accent/10 transition-[background-color,border-color] duration-500">
                          <Show
                            when={
                              isSigningOut() ||
                              ui().showSpinner ||
                              !appState.isReady() ||
                              ctx.db() === null
                            }
                          >
                            <Loader2 class="size-7 animate-spin text-accent shrink-0" />
                          </Show>
                        </div>
                      }
                    >
                      <div class="relative flex size-16 items-center justify-center rounded-2xl border border-destructive/30 bg-destructive/15 transition-[background-color,border-color] duration-500">
                        <WifiOff class="size-7 text-destructive shrink-0" />
                      </div>
                    </Show>
                    <div class="absolute -inset-4 bg-accent/5 rounded-full blur-2xl -z-10 pointer-events-none" />
                  </div>

                  {/* Status text */}
                  <div class="space-y-1.5 w-full">
                    <h2 class="text-lg font-bold tracking-tight">
                      {title() || t(msg`Setting up your workspace...`)}
                    </h2>
                    <p class="text-sm text-muted-foreground min-h-[1.25rem]">
                      {description() || t(msg`Syncing your data...`)}
                    </p>
                  </div>

                  {/* Progress bar */}
                  <Show when={!isSigningOut() && ui().showProgress && displayedPercent() !== null}>
                    <div
                      class="w-full pt-1"
                      data-testid="initial-sync-progress"
                      data-percent={progressPercent()}
                      data-downloaded={status().downloadedOperations ?? ""}
                      data-total={status().totalOperations ?? ""}
                      data-streams-synced={status().syncedStreamCount}
                      data-streams-total={status().streamCount}
                    >
                      <div class="h-1.5 w-full overflow-hidden rounded-full bg-secondary/50">
                        <div
                          class="h-full w-full bg-accent origin-left transition-transform duration-500 ease-out"
                          style={{ transform: `scaleX(${progressPercent() / 100})` }}
                        />
                      </div>
                      <p class="mt-2 text-xs text-muted-foreground">{progressPercent()}%</p>
                    </div>
                  </Show>

                  {/* Actions */}
                  <Show when={ui().showActions}>
                    <div class="flex w-full flex-col gap-2.5 pt-2">
                      <Button class="w-full" onClick={handleMinimize} disabled={isSigningOut()}>
                        {t(msg`Sync in background`)}
                      </Button>
                      <Button
                        class="w-full"
                        variant="outline"
                        onClick={() => {
                          promptQuickSignOut();
                        }}
                        disabled={isChecking() || isSigningOut()}
                      >
                        <Show when={isChecking() || isSigningOut()} fallback={t(msg`Sign out`)}>
                          <Loader2 class="h-4 w-4 animate-spin mr-2" />
                          {isSigningOut() ? t(msg`Signing out...`) : t(msg`Checking...`)}
                        </Show>
                      </Button>
                    </div>
                  </Show>
                </div>
              </GlassSurface>
            </div>
          </Show>
        </Transition>
      </Portal>
    </div>
  );
};
